import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Tag,
  User,
} from "lucide-react";
import Seo from "../components/Seo";
import RetreatInquiryForm from "../components/RetreatInquiryForm";
import FreeConsultationSection from "../components/FreeConsultationSection";
import { fetchOfferingBySlug, fetchRetreatBySlug } from "../services/api";
import { getOfferingListingPath } from "../lib/offeringRoutes";
import { ONLINE_COURSE } from "../lib/courseTypes";
import TeacherScheduleBlock from "../components/TeacherScheduleBlock";
import { formatScheduleLines } from "../lib/scheduleDisplay";
import { getTeacherImage } from "../lib/teacherImages";
import { useCurrency } from "../context/CurrencyContext";
import { formatFromInr } from "../lib/currency";
import { resolveBasePriceInr } from "../lib/retreatSlug";
import { SUPPORTED_CURRENCIES } from "../lib/currencyConfig";

function TeacherOfferingDetailPage({ listingType = ONLINE_COURSE }) {
  const { slug } = useParams();
  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redirectTo, setRedirectTo] = useState("");
  const { currency, setCurrency, rates } = useCurrency();

  const listingPath = getOfferingListingPath(listingType);
  const listingLabel = listingType === "Prenatal Yoga" ? "Prenatal Yoga" : "Online Courses";

  useEffect(() => {
    setLoading(true);
    setError("");
    setRedirectTo("");
    fetchOfferingBySlug(slug)
      .then((data) => setOffering(data))
      .catch(async () => {
        try {
          await fetchRetreatBySlug(slug);
          setRedirectTo(`/retreats/${slug}`);
        } catch {
          setError("Could not load course details. Please try again.");
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error || !offering) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-gray-600 mb-4">{error || "Course not found."}</p>
          <Link to={listingPath} className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">
            <ArrowLeft size={16} /> Back to {listingLabel}
          </Link>
        </div>
      </div>
    );
  }

  const location = [offering.city, offering.country].filter(Boolean).join(", ") || "India";
  const scheduleLines = formatScheduleLines(offering.schedule_json);
  const amountInr = resolveBasePriceInr(
    {
      price_inr: offering.price_inr,
      price_usd: offering.price_usd,
      price_gbp: offering.price_gbp,
      price_eur: offering.price_eur,
      price_clean: offering.price_inr ?? offering.price,
      price_source_currency: offering.currency,
      price_source_amount: offering.price,
    },
    rates
  );
  const priceLabel = amountInr != null ? formatFromInr(amountInr, currency, rates) : null;
  const hasPrice = Boolean(priceLabel);
  const canonicalPath = `${listingPath}/${slug}`;
  const pageTitle = offering.title;
  const pageDescription = `${offering.title} with ${offering.teacher_name} in ${location}. ${offering.format || "Live online class"} · ${offering.duration || "Flexible duration"}.`;

  const teacherPhoto = getTeacherImage({
    teacher_slug: offering.teacher_slug,
    teacher_id: offering.teacher_id,
    profile_image: offering.profile_image,
  });
  const inquiryRetreat = {
    retreat_key: offering.offering_key,
    retreat_name: `${offering.title} with ${offering.teacher_name}`,
    teacher_name: offering.teacher_name,
    slug: offering.slug,
    location,
    source: "yoga_teachers",
    price_inr: amountInr,
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalPath={canonicalPath}
        ogImagePath={teacherPhoto}
      />

      <div className="relative h-72 md:h-96 bg-gray-900 overflow-hidden">
        <img
          src={teacherPhoto}
          alt={`${offering.teacher_name} — ${pageTitle}`}
          className="w-full h-full object-cover opacity-70"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to={listingPath}
              className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-3 transition-colors"
            >
              <ArrowLeft size={14} /> {listingLabel}
            </Link>
            <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-2">{pageTitle}</h1>
            <p className="flex items-center gap-1.5 text-gray-300 text-sm font-medium mb-2">
              <MapPin size={14} className="text-emerald-400" />
              {location}
            </p>
            <p className="text-emerald-200 text-sm font-semibold">with {offering.teacher_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {offering.duration && (
                  <div className="flex items-start gap-2">
                    <Clock size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Duration</p>
                      <p className="text-sm font-semibold text-gray-800">{offering.duration}</p>
                    </div>
                  </div>
                )}
                {offering.format && (
                  <div className="flex items-start gap-2">
                    <Calendar size={18} className="text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Format</p>
                      <p className="text-sm font-semibold text-gray-800">{offering.format}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(offering.category || offering.listing_type) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Tag size={14} /> Course details
                </h2>
                <div className="flex flex-wrap gap-2">
                  {offering.listing_type && (
                    <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100">
                      {offering.listing_type}
                    </span>
                  )}
                  {offering.category && (
                    <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full border border-emerald-100">
                      {offering.category}
                    </span>
                  )}
                </div>
              </div>
            )}

            {scheduleLines.length > 0 && (
              <TeacherScheduleBlock
                lines={scheduleLines.map((entry) => ({
                  title: "",
                  schedule:
                    entry.label ||
                    [entry.days, entry.time, entry.timezone].filter(Boolean).join(" · "),
                }))}
              />
            )}

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <User size={18} className="text-emerald-500" /> About the teacher
              </h2>
              <p className="text-2xl font-black text-gray-900 mb-2">{offering.teacher_name}</p>
              {offering.headline && (
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{offering.headline}</p>
              )}
              {offering.teacher_slug && (
                <Link
                  to={`/teachers/${offering.teacher_slug}`}
                  className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600 hover:text-emerald-700"
                >
                  View full teacher profile
                </Link>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              {hasPrice ? (
                <>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Price</p>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-3xl font-black text-emerald-600">
                      {priceLabel}
                    </div>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    >
                      {SUPPORTED_CURRENCIES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Pricing</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Contact us for the latest class fee and enrollment details.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Instructor</p>
              <p className="text-sm font-semibold text-gray-900">{offering.teacher_name}</p>
              <p className="text-xs text-gray-500 mt-1">{location}</p>
            </div>
          </div>
        </div>

        <FreeConsultationSection className="mt-12" context={inquiryRetreat} />

        <section className="mt-12 scroll-mt-28 relative">
          <div id="enquire" className="absolute -top-28" aria-hidden="true" />
          <div id="book-enquire" className="absolute -top-28" aria-hidden="true" />
          <RetreatInquiryForm variant="section" retreat={inquiryRetreat} />
        </section>
      </div>
    </div>
  );
}

export default TeacherOfferingDetailPage;
